import * as React from 'react';
import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Box,
  Typography,
  Container,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { roles, url } from '../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signUpSchema } from '../forms/schemas';
import PasswordStrength from '../forms/PasswordStrength';
import { toast } from 'sonner';
import Telefone from '../utils/Telefone';
import { validateInput } from '../utils/validates';
import {
  Visibility,
  VisibilityOff,
  PersonAdd,
  Person,
  Business,
  Email,
  Phone,
  Lock,
  Security
} from '@mui/icons-material';
import logoImage from '../../logo192.png';



const theme = createTheme();

// Styled components para elementos modernos
const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '25px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 32px',
  fontSize: '1.1rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '15px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,1)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255,255,255,1)',
      transform: 'translateY(-1px)',
    },
  },
}));

const ModernSelect = styled(Select)(({ theme }) => ({
  borderRadius: '15px',
  backgroundColor: 'rgba(255,255,255,0.9)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,1)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-focused': {
    backgroundColor: 'rgba(255,255,255,1)',
    transform: 'translateY(-1px)',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));



export default function SignUp({ usePageTitle }) {
  usePageTitle('PixTick | Signup');
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [empresas, setEmpresas] = React.useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = React.useState(false);
  let navigate = useNavigate();
  const { isLoggedIn, logout, user } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      fname: '',
      lname: '',
      role: 'USER',
      empresa: '',
      cnpj: '',
      empresaId: '',
      email: '',
      phone: '',
      password: ''
    }
  });

  const watchedRole = watch('role');
  const watchedPassword = watch('password');

  React.useEffect(() => {
    // Redirecionar se não estiver logado
    if (!isLoggedIn) {
      navigate('/signin');
    }
  }, [isLoggedIn, navigate]);

  // Carregar empresas (usuários ADMIN) quando o componente monta
  React.useEffect(() => {
    const loadEmpresas = async () => {
      setLoadingEmpresas(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: "Bearer " + token };
        const response = await axios.get(url + "/api/v1/user/empresas", { headers });
        setEmpresas(response.data.data || []);

        // Se o usuário logado for ADMIN, pré-selecionar sua própria empresa
        if (user && user.role === 'ADMIN') {
          const minhaEmpresa = (response.data.data || []).find(emp => emp.email === user.email);
          if (minhaEmpresa) {
            setValue('empresaId', minhaEmpresa._id);
          }
        }
      } catch (error) {
        console.error('❌ Erro ao carregar empresas:', error);
        console.error('❌ Status:', error.response?.status);
        console.error('❌ Data:', error.response?.data);
        toast.error('Erro ao carregar empresas');
      } finally {
        setLoadingEmpresas(false);
      }
    };

    if (isLoggedIn) {
      loadEmpresas();
    }
  }, [isLoggedIn, user, setValue]);

  const [state, setState] = React.useState({
    ddi: '+55',
    phone: '',
    error: '',
    fname: '',
    lname: '',
    email: '',
    password: '',
    empresa: '',
    cnpj: ''
  });

  const formatCNPJ = (value) => {
    return value
      .replace(/\D/g, '') // Remove tudo que não for número
      .replace(/^(\d{2})(\d)/, '$1.$2') // Adiciona ponto após os 2 primeiros números
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3') // Adiciona ponto após os próximos 3 números
      .replace(/\.(\d{3})(\d)/, '.$1/$2') // Adiciona barra após os próximos 3 números
      .replace(/(\d{4})(\d)/, '$1-$2') // Adiciona traço antes dos últimos 2 números
      .slice(0, 18); // Garante que não ultrapasse 18 caracteres (formato do CNPJ)
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const token = await localStorage.getItem("token");

      // Preparar dados base
      const form = {
        fullname: data.fname + " " + data.lname,
        email: data.email,
        numero: (state.ddi + data.phone).replace(/[()\s+-]/g, ""),
        password: data.password,
        role: data.role
      };

      // Se for ADMIN, adicionar dados da empresa
      if (data.role === 'ADMIN') {
        form.empresa = data.empresa;
        form.cnpj = data.cnpj;
      } else if (data.role === 'USER') {
        // Se for USER, adicionar ID da empresa selecionada
        form.empresaId = data.empresaId;
        // Buscar dados da empresa selecionada (usuário ADMIN)
        const empresaSelecionada = empresas.find(emp => emp._id === data.empresaId);
        if (empresaSelecionada) {
          form.empresa = empresaSelecionada.empresa;
          form.cnpj = empresaSelecionada.cnpj;
        }
      }

      const headers = {
        Authorization: "Bearer " + token,
      };
      await axios.post(url + "/api/v1/user/signup", form, { headers });

      toast.success("Usuário cadastrado com sucesso!", {
        description: "Redirecionando para a página de login...",
        duration: 3000,
      });

      setTimeout(() => {
        navigate("/signin");
      }, 1500);

    } catch (error) {
      console.error(error);

      const errorMessage = error.response?.data?.message || "Erro ao cadastrar usuário";

      if (errorMessage.includes("already exists")) {
        toast.error("Usuário já existe!", {
          description: "Este email já está cadastrado no sistema. Tente fazer login ou use outro email.",
          duration: 5000,
        });
      } else {
        toast.error("Erro ao cadastrar usuário", {
          description: errorMessage,
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, sm: 2 },
        // Ajustar para mobile e desktop
        pt: { xs: 'calc(64px + max(env(safe-area-inset-top, 0px), 10px))', sm: 8 },
        pb: { xs: 2, sm: 4 }
      }}>
        <Card
          elevation={10}
          sx={{
            maxWidth: 600,
            width: '100%',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255,255,255,0.95)',
            // Ajustar para mobile - mesmo padrão do perfil
            maxWidth: { xs: '100%', sm: 600 },
            margin: { xs: 0, sm: 2 }
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                textAlign: 'center',
                mb: 3,
                fontWeight: 600,
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <PersonAdd color="primary" />
              Novo Usuário
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="fname"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <ModernTextField
                        {...field}
                        autoComplete="given-name"
                        required
                        fullWidth
                        id="fname"
                        label="Nome"
                        autoFocus
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="lname"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <ModernTextField
                        {...field}
                        required
                        fullWidth
                        id="lname"
                        label="Sobrenome"
                        autoComplete="family-name"
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth error={!!error}>
                        <InputLabel id="role-label">Nível de Acesso</InputLabel>
                        <ModernSelect
                          {...field}
                          labelId="role-label"
                          id="role"
                          label="Nível de Acesso"
                          startAdornment={
                            <InputAdornment position="start">
                              <Security color="action" />
                            </InputAdornment>
                          }
                        >
                          {roles.map((role) => (
                            <MenuItem key={role.value} value={role.value}>
                              {role.label}
                            </MenuItem>
                          ))}
                        </ModernSelect>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Campos condicionais baseados no nível de acesso */}
                {watchedRole === 'ADMIN' && (
                  <>
                    <Grid item xs={12}>
                      <Controller
                        name="empresa"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <ModernTextField
                            {...field}
                            required
                            fullWidth
                            id="empresa"
                            label="Nome da Empresa"
                            autoComplete="empresa"
                            error={!!error}
                            helperText={error?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Business color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="cnpj"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <ModernTextField
                            {...field}
                            required
                            fullWidth
                            id="cnpj"
                            label="CNPJ"
                            autoComplete="cnpj"
                            error={!!error}
                            helperText={error?.message}
                            onChange={(e) => {
                              const formatted = formatCNPJ(e.target.value);
                              field.onChange(formatted);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Business color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                {watchedRole === 'USER' && (
                  <Grid item xs={12}>
                    <Controller
                      name="empresaId"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth error={!!error}>
                          <InputLabel id="empresa-label">Empresa</InputLabel>
                          <ModernSelect
                            {...field}
                            labelId="empresa-label"
                            id="empresaId"
                            label="Empresa"
                            disabled={loadingEmpresas || (user && user.role === 'ADMIN')}
                            startAdornment={
                              <InputAdornment position="start">
                                <Business color="action" />
                              </InputAdornment>
                            }
                          >
                            {loadingEmpresas ? (
                              <MenuItem disabled>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Carregando empresas...
                              </MenuItem>
                            ) : empresas.length === 0 ? (
                              <MenuItem disabled>
                                Nenhuma empresa cadastrada
                              </MenuItem>
                            ) : (
                              empresas.map((empresa) => (
                                <MenuItem key={empresa._id} value={empresa._id}>
                                  {empresa.empresa}{empresa.cnpj ? ` - ${empresa.cnpj}` : ''}
                                </MenuItem>
                              ))
                            )}
                          </ModernSelect>
                          {error && <FormHelperText>{error.message}</FormHelperText>}
                          {user && user.role === 'ADMIN' && (
                            <FormHelperText>
                              Usuário será vinculado à sua empresa: {user.empresa}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <ModernTextField
                        {...field}
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        autoComplete="email"
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <ModernTextField
                        {...field}
                        required
                        fullWidth
                        id="phone"
                        label="Telefone"
                        autoComplete="tel"
                        placeholder="(xx) xxxxx-xxxx"
                        error={!!error}
                        helperText={error?.message}
                        onChange={(e) => {
                          // Aplicar máscara de telefone
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 11) {
                            value = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
                            value = value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
                          }
                          field.onChange(value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <ModernTextField
                        {...field}
                        required
                        fullWidth
                        label="Senha"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="new-password"
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    )}
                  />
                  <PasswordStrength password={watchedPassword} />
                </Grid>

              </Grid>

              <Divider sx={{ my: 3 }} />

              <ModernButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting || loading}
                sx={{
                  backgroundColor: '#1976D2',
                  '&:hover': {
                    backgroundColor: '#1565C0',
                  }
                }}
              >
                {(isSubmitting || loading) ? <CircularProgress size={24} color="inherit" /> : 'Cadastrar'}
              </ModernButton>

              {state.error && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  borderRadius: 2,
                  border: '1px solid #F44336'
                }}>
                  <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                    {state.error}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
